/**
 * patternMatch
 *
 * Reine, framework-freie Suche nach Wiederholungen der aktuellen Auswahl (kein OSMD/Vue) – die
 * Entscheidungslogik des Pattern-Layers von {@link @/composables/osmd/useScoreHighlight}. Generisch
 * über den Notentyp `N`, damit sie ohne OSMD-Mock unit-testbar ist; die Lanes aus der gerenderten
 * Partitur baut `useScoreHitTesting.patternLanes`.
 *
 * **Was als „gleiches Pattern" gilt (Nutzervorgabe: Tonhöhe + Rhythmus exakt):**
 * - Eine **Lane** ist eine Stimme einer Notenzeile, chronologisch über alle Takte; ein **Event**
 *   ist eine Zählzeit dieser Stimme (Akkordtöne zusammen) mit ihrer Notenlänge.
 * - Das Pattern sind je Lane die ausgewählten Events in Reihenfolge – nur, wenn sie **lückenlos**
 *   aufeinanderfolgen und es **mindestens zwei** sind. Ein einzelnes Event würde sonst jede
 *   gleiche Note des Stücks markieren; eine verstreute Auswahl (Strg-Klicks) ist kein Motiv.
 * - Ein Treffer ist eine zusammenhängende Event-Folge irgendeiner Lane (alle Notenzeilen werden
 *   durchsucht), deren Events **dieselbe Länge** haben und **alle Tonhöhen** des Pattern-Events
 *   enthalten (Obermenge). So findet eine Teilauswahl im Akkord – etwa nur die Oberstimme – die
 *   Linie auch dort, wo sie harmonisiert ist; markiert werden nur die passenden Köpfe.
 *   Transponierte Sequenzen zählen bewusst NICHT (exakte Tonhöhe inkl. Oktave). Was „gleicher
 *   Ton" heißt, legt der Schlüssel {@link PatternNote.key} des Aufrufers fest.
 * - Pausen sind Events: eine Pause passt nur auf eine Pause gleicher Länge.
 * - Überlappende Treffer sind erlaubt; das Quell-Vorkommen selbst wird übersprungen.
 */

export interface PatternNote<N> {
    note: N;
    /** Vergleichsschlüssel des Tons (gleicher Schlüssel = gleicher Ton); `null` = Pause. Opak,
     *  damit der Aufrufer bestimmt, was „gleich" heißt – `patternLanes` nimmt Halbton +
     *  Notenkopfform (siehe dort). */
    key: string | null;
}

/** Eine Zählzeit einer Stimme: Akkordtöne zusammen, gemeinsame Notenlänge (in Ganzen). */
export interface PatternEvent<N> {
    length: number;
    notes: PatternNote<N>[];
}

/** Eine Stimme einer Notenzeile, chronologisch über das ganze Stück. */
export type PatternLane<N> = PatternEvent<N>[];

/** Ein Pattern-Element: Länge + die Schlüssel der ausgewählten Töne; `rest` = Pausen-Event. */
interface PatternStep {
    length: number;
    rest: boolean;
    keys: string[];
}

interface Pattern {
    lane: number;
    start: number;
    steps: PatternStep[];
}

/** Längenvergleich mit Toleranz: OSMDs `Fraction.RealValue` ist eine Gleitkommazahl (Triolen). */
const LENGTH_EPSILON = 1e-9;

function isRestEvent<N>(event: PatternEvent<N>): boolean {
    return event.notes.length > 0 && event.notes.every(n => n.key === null);
}

/** Patterns der Auswahl, je Lane höchstens eines (siehe Modul-Docstring: lückenlos, ≥ 2 Events). */
function patternsFromSelection<N>(lanes: PatternLane<N>[], selected: ReadonlySet<N>): Pattern[] {
    const patterns: Pattern[] = [];
    lanes.forEach((lane, laneIndex) => {
        const hit: number[] = [];
        lane.forEach((event, i) => {
            if (event.notes.some(n => selected.has(n.note))) hit.push(i);
        });
        if (hit.length < 2) return;
        const start = hit[0];
        if (hit[hit.length - 1] - start !== hit.length - 1) return;
        const steps = hit.map((i): PatternStep => {
            const event = lane[i];
            const chosen = event.notes.filter(n => selected.has(n.note));
            return {
                length: event.length,
                rest: chosen.every(n => n.key === null),
                keys: chosen.flatMap(n => (n.key === null ? [] : [n.key])),
            };
        });
        patterns.push({lane: laneIndex, start, steps});
    });
    return patterns;
}

/** Passende Noten eines Events für einen Pattern-Schritt; `null` = kein Treffer. */
function matchEvent<N>(event: PatternEvent<N>, step: PatternStep): N[] | null {
    if (Math.abs(event.length - step.length) > LENGTH_EPSILON) return null;
    if (step.rest) return isRestEvent(event) ? event.notes.map(n => n.note) : null;
    const out: N[] = [];
    for (const key of step.keys) {
        const found = event.notes.find(n => n.key === key);
        if (!found) return null;
        out.push(found.note);
    }
    return out;
}

/** Alle Noten, die an anderer Stelle des Stücks ein Pattern der Auswahl wiederholen (Regeln im
 *  Modul-Docstring). Leeres Ergebnis, wenn die Auswahl kein Pattern bildet. Jede Note höchstens
 *  einmal, in Fundreihenfolge. */
export function findPatternMatches<N>(lanes: PatternLane<N>[], selected: ReadonlySet<N>): N[] {
    const patterns = patternsFromSelection(lanes, selected);
    if (patterns.length === 0) return [];
    const result = new Set<N>();
    for (const pattern of patterns) {
        const size = pattern.steps.length;
        lanes.forEach((lane, laneIndex) => {
            for (let start = 0; start + size <= lane.length; start++) {
                if (laneIndex === pattern.lane && start === pattern.start) continue;
                const notes: N[] = [];
                let ok = true;
                for (let k = 0; k < size; k++) {
                    const matched = matchEvent(lane[start + k], pattern.steps[k]);
                    if (!matched) {
                        ok = false;
                        break;
                    }
                    notes.push(...matched);
                }
                if (ok) for (const n of notes) result.add(n);
            }
        });
    }
    return [...result];
}
